import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import { FunctionField, TextField, TopToolbar, useRecordContext, usePermissions, useTranslate } from 'react-admin';
import { Box, Chip, useTheme, useMediaQuery, Popper, Tooltip } from '@mui/material';
import CustomExportButton from '@/components/global/buttons/CustomExportButton.jsx';
import React, { useState, useMemo } from 'react';
import { DeviceStateField } from '@/components/device/fields/DeviceStateField.jsx';
import DeviceIssueErrorDetailsModal from '@/components/deviceIssue/DeviceIssueErrorDetailsModal';
import DeviceCollectionPointField from '@/components/device/DeviceCollectionPointField.jsx';
import PropTypes from 'prop-types';
import InfoIcon from '@/assets/icons/info-icon.svg?react';
import ErrorIcon from '@/assets/icons/error-icon.svg?react';
import ErrorSecondLevelIcon from '@/assets/icons/error-second-level-icon.svg?react';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { deviceStateChoices, errorLevelChoices } from '@/components/global/options/selectOptions.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover.jsx';
import { ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import { DeactivateDeviceActionButton, FixDeviceProblemButton, RestartDeviceActionButton } from '@/components/global/buttons/actionButtons.jsx';
import { DeviceIssueBulkActionButtons } from '@/components/global/buttons/bulkButtons.jsx';
import ServiceAddIcon from '@/assets/icons/service-add-icon.svg?react';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import { useDrawer } from '@/composables/useDrawer';
import { styled } from '@mui/material/styles';
import DeviceUptimeField from '@/components/device/DeviceUptimeField';
import DeviceTransactionsField from '@/components/device/DeviceTransactionsField';
import DeviceServiceStatusField from '@/components/device/DeviceServiceStatusField';
import { format } from 'date-fns';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput.jsx';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import { Palette } from '@/themes/themeOverrides/Palette';
import TableHeaderCellWithSubtitle from '@/components/global/fields/TableHeaderCellWithSubtitle.jsx';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';
import CachedDataActionButton from '@/components/global/buttons/CachedDataActionButton.jsx';

const StyledDeviceIssueList = styled('div')(({ theme }) => ({
    '& .RaDatagrid-root': {
        '&.responsive-table': {
            '& .device-error-field': {
                display: 'flex',
                fontSize: '11px',
                width: 'auto',
                lineHeight: '16px',
                '& .error-title-container': {
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                    [theme.breakpoints.up('lg')]: {
                        margin: '4px 0 0',
                    },
                    '& span': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        minWidth: 0,
                        width: '100%',
                        maxWidth: '400px',
                    },
                },
                '& .info-icon': {
                    display: 'none',
                },
                '& .error-time': {
                    display: 'block',
                },
                '& span': {
                    fontSize: '11px',
                    lineHeight: '16px',
                },
                '& .MuiChip-root': {
                    borderRadius: '6px',
                    height: '18px',
                    padding: '0',
                },
                '& .MuiChip-label': {
                    padding: '0',
                    fontWeight: 600,
                },
                [theme.breakpoints.up('lg')]: {
                    display: 'block',
                    width: 'auto',
                    '& .info-icon': {
                        display: 'flex',
                    },
                    '& .MuiChip-root': {
                        padding: '0 6px',
                    },
                },
            },
        },
        '& .serial-number-mobile': {
            padding: '12px 0 22px',
            fontWeight: 600,
            [theme.breakpoints.up('lg')]: {
                padding: 'inherit',
                fontWeight: 'inherit',
            },
        },
        '& .more-actions-buttons': {
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '8px',
            '& > button': {
                fontSize: '11px',
                fontWeight: 500,
            },
            '& > button:first-of-type': {
                width: '100%',
                [theme.breakpoints.up('lg')]: {
                    width: 'max-content',
                },
            },
            '& .MuiButtonBase-root': {
                width: 36,
                height: 36,
            },
        },
    },
}));

const ERROR_ICONS = {
    1: ErrorIcon,
    2: ErrorSecondLevelIcon,
};

const ErrorPopper = ({ open, anchorEl, record, onMouseEnter, onMouseLeave }) => (
    <Popper
        id={open ? 'error-details-popper' : undefined}
        open={open}
        anchorEl={anchorEl}
        placement='bottom'
        sx={{
            inset: '14px auto auto 0px !important',
            zIndex: 9999,
            '& ::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: '-8px',
                left: '50%',
                transform: 'translateX(-50%) rotate(45deg)',
                width: '16px',
                height: '16px',
                backgroundColor: Palette.white,
            },
            '& ::after': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: '-14px',
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'transparent',
            },
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
    >
        <DeviceIssueErrorDetailsModal record={record} />
    </Popper>
);

export const DeviceIssueList = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const { openDrawer } = useDrawer();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();

    const deviceStateFilter = useMemo(
        () => (
            <CustomSelectArrayInput
                key='filter_state'
                source='state_in'
                label={translate('resources.deviceIssue.labels.stateFilter')}
                choices={deviceStateChoices}
                alwaysOn={isMobile}
                forceDesktopOnMobile={true}
            />
        ),
        [isMobile, translate],
    );

    const contractorFilter = useMemo(
        () => (
            <ReferenceArrayFilter
                key='contractor'
                source='collectionPoint->contractorId_in'
                reference='contractor'
                label={translate('resources.deviceIssue.labels.contractorFilter')}
                alwaysOn={isMobile}
            />
        ),
        [isMobile, translate],
    );

    const filters = useMemo(
        () => (permissions.isClient ? [deviceStateFilter] : [deviceStateFilter, contractorFilter]),
        [permissions.isClient, deviceStateFilter, contractorFilter],
    );

    const searchBar = <SearchBar key='search' source='value' label={translate('common.search')} alwaysOn />;

    if (isLoading) return null;

    const canBulkManageDevices = permissions.isAdmin || permissions.isServiceMaster;

    return (
        <StyledDeviceIssueList>
            <CustomList
                filters={[searchBar, ...filters]}
                filterDefaultValues={{}}
                sort={{ field: 'occurredAt', order: 'DESC' }}
                actions={<ListActions />}
                addLastColumnShadow={isDesktop}
                paginationClassName='pagination-rounded-bottom'
            >
                <CustomDatagridConfigurable
                    header={<CustomDatagridHeader />}
                    bulkActionButtons={canBulkManageDevices ? <DeviceIssueBulkActionButtons /> : false}
                    className='responsive-table'
                    rowClick={id => {
                        openDrawer(id, 'device-issue-show');
                        return false;
                    }}
                >
                    <FunctionField
                        label={translate('resources.deviceIssue.labels.collectionPoint')}
                        sortBy='collectionPoint.customId'
                        render={record => <DeviceCollectionPointField collectionPoint={record.collectionPoint} />}
                    />
                    {!isDesktop && (
                        <FunctionField
                            label={translate('resources.deviceIssue.labels.serialNumber')}
                            source='serialNumber'
                            onClick={e => e.stopPropagation()}
                            render={record => <DrawerLink resource='device' action='show' source={record.id} label={record.serialNumber} relative />}
                        />
                    )}
                    <FunctionField
                        label={translate('resources.deviceIssue.labels.deviceState')}
                        sortBy='state'
                        render={record => <DeviceStateField record={record} className='device-status-field' />}
                    />
                    <FunctionField
                        label={translate('resources.deviceIssue.labels.cause')}
                        sortBy='error.code'
                        render={record => <DeviceIssueErrorField className='device-status-field device-error-field' record={record} />}
                    />
                    {isDesktop && (
                        <FunctionField
                            source='serialNumber'
                            label={translate('resources.deviceIssue.labels.serialNumber')}
                            onClick={e => e.stopPropagation()}
                            render={record => <DrawerLink resource='device' action='show' source={record.id} label={record.serialNumber} relative />}
                        />
                    )}
                    {isDesktop && (
                        <FunctionField
                            source='analytics.uptime'
                            label={
                                <TableHeaderCellWithSubtitle
                                    title={translate('common.uptime')}
                                    sortByLabel={translate('common.uptime')}
                                    subTitle={`(${translate('analytics.common.daysWithCount', { count: 7 })})`}
                                />
                            }
                            render={record => <DeviceUptimeField className='' record={record} defaultPeriod={7} />}
                        />
                    )}
                    {isDesktop && (
                        <FunctionField
                            source='analytics.transactionsCount'
                            label={
                                <TableHeaderCellWithSubtitle
                                    title={translate('common.transactionsCount')}
                                    sortByLabel={translate('common.transactionsCount')}
                                    subTitle={`(${translate('analytics.common.daysWithCount', { count: 7 })})`}
                                />
                            }
                            render={record => <DeviceTransactionsField record={record} defaultPeriod={7} />}
                        />
                    )}
                    <FunctionField
                        label={translate('resources.deviceIssue.labels.serviceStatus')}
                        sortBy='occurredAt'
                        render={record => (
                            <DeviceServiceStatusField
                                className='device-status-field device-status-field-last'
                                formatSince={record.occurredAt}
                                statusText={translate('resources.deviceIssue.labels.waitingForTriage')}
                            />
                        )}
                    />
                    {isDesktop && (
                        <FunctionField
                            label={translate('resources.deviceIssue.labels.premium')}
                            sortBy='collectionPoint.premiumService'
                            render={record => (record?.collectionPoint?.premiumService ? translate('common.boolean.yes') : translate('common.boolean.no'))}
                        />
                    )}
                    <FunctionField
                        label={translate('common.options')}
                        render={record => (
                            <Box onClick={e => e.stopPropagation()}>
                                <MoreActionsPopover errorId={record.id} />
                            </Box>
                        )}
                    />
                </CustomDatagridConfigurable>
            </CustomList>
        </StyledDeviceIssueList>
    );
};

const ListActions = () => {
    const { permissions } = usePermissions();
    return (
        <TopToolbar>
            <SearchFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            <CustomToolbarActionsPopover>
                <CustomExportButton />
                {permissions.isAdmin && <CachedDataActionButton resource='device' type='Analytics' />}
                {permissions.isAdmin && <CachedDataActionButton resource='device-service' type='Service' />}
            </CustomToolbarActionsPopover>
        </TopToolbar>
    );
};

export const MoreOptionsPopover = ({ record, buttonLabel }) => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const translate = useTranslate();

    if (isLoading || !record) return null;

    const openDeviceDrawer = () => {
        openDrawer(record.id, 'device-show');
    };

    return (
        <CustomMorePopover record={record} buttonLabel={buttonLabel} sx={{ zIndex: 1200 }}>
            {(permissions.isAdmin || permissions.isServiceMaster) && <FixDeviceProblemButton id={record.id} buttonProps={{ 'data-keep-popover-open': true }} />}
            <CustomButton variant='text' label={translate('resources.deviceIssue.labels.openDeviceDetails')} onClick={openDeviceDrawer}>
                <ServiceAddIcon />
            </CustomButton>
            {(permissions.isAdmin || permissions.isServiceMaster) && (
                <RestartDeviceActionButton id={record.id} buttonProps={{ 'data-keep-popover-open': true }} />
            )}
            {(permissions.isAdmin || permissions.isServiceMaster) && (
                <DeactivateDeviceActionButton id={record.id} buttonProps={{ 'data-keep-popover-open': true }} />
            )}
        </CustomMorePopover>
    );
};

const DeviceIssueModel = PropTypes.shape({
    id: PropTypes.string.isRequired,
    collectionPointId: PropTypes.string,
    collectionPoint: PropTypes.shape({
        id: PropTypes.string.isRequired,
        contractorId: PropTypes.string.isRequired,
        serviceCompanyId: PropTypes.string,
        contractor: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        }).isRequired,
        customId: PropTypes.string.isRequired,
        premiumService: PropTypes.bool.isRequired,
        address: PropTypes.shape({
            address: PropTypes.string.isRequired,
            city: PropTypes.string.isRequired,
            postalCode: PropTypes.string.isRequired,
            country: PropTypes.string.isRequired,
        }).isRequired,
        contact: PropTypes.shape({
            contactPhoneNumber: PropTypes.string.isRequired,
        }).isRequired,
    }),
    serialNumber: PropTypes.string,
    state: PropTypes.string,
    occurredAt: PropTypes.string,
    error: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        level: PropTypes.number,
        title: PropTypes.string,
        solutionTip: PropTypes.string,
        time: PropTypes.string,
        details: PropTypes.string,
    }),
});

MoreOptionsPopover.propTypes = {
    record: DeviceIssueModel,
    buttonLabel: PropTypes.string,
};

const MoreActionsPopover = () => {
    const record = useRecordContext();
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const translate = useTranslate();

    if (isLoading || !record) return null;

    const openCreateDrawer = () => {
        openDrawer(record.id, 'service-request-create');
    };

    return (
        <Box className='more-actions-buttons'>
            {(permissions.isAdmin || permissions.isServiceMaster) && (
                <CustomButton
                    variant='contained'
                    label={translate('resources.deviceIssue.labels.createServiceRequest')}
                    sx={{ width: '100%' }}
                    onClick={openCreateDrawer}
                />
            )}
            <MoreOptionsPopover record={record} />
        </Box>
    );
};

export const DeviceIssueErrorField = ({ record, className, isUnified = false }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const translate = useTranslate();
    const [anchorEl, setAnchorEl] = useState(null);

    if (!record?.error) {
        return <span className={className}>{translate('common.empty')}</span>;
    }

    const { error } = record;
    const errorLevelNumber = error.level ?? 2;

    const errorLevel = errorLevelChoices.find(e => e.id === errorLevelNumber);

    const handleMouseEnter = event => {
        if (!isDesktop) return;
        setAnchorEl(event.currentTarget.closest('.error-field-container'));
    };

    const handleMouseLeave = () => {
        if (!isDesktop) return;
        setAnchorEl(null);
    };

    const ErrorIcon = !isDesktop && ERROR_ICONS[errorLevelNumber];

    return (
        <Box className={className}>
            <div
                className='error-field-container'
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'left', position: 'relative', width: 'fit-content' }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {ErrorIcon && <ErrorIcon className='error-icon' style={{ marginRight: '8px' }} />}
                <Chip
                    size='small'
                    label={
                        <span>
                            Error: {error.code}
                            {!isDesktop && !isUnified ? ',' : ''}
                        </span>
                    }
                    sx={{
                        backgroundColor: isDesktop || isUnified ? errorLevel.color : 'transparent',
                        marginRight: '5px',
                    }}
                    className={isUnified ? 'chip-small' : ''}
                />
                <span className='info-icon'>
                    <InfoIcon />
                </span>
                {isDesktop && (
                    <ErrorPopper
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        record={record}
                        onMouseEnter={() => setAnchorEl(anchorEl)}
                        onMouseLeave={handleMouseLeave}
                    />
                )}
            </div>
            <div className='error-title-container'>
                <TextField source='error.title' emptyText={translate('common.errors.unknownError')} />
            </div>
            {(isDesktop || isUnified) && record?.error?.time && (
                <Tooltip title={formatIntervalSince(record.error.time)} arrow classes={{ tooltip: 'tooltip-black' }}>
                    <span className='error-time'>{format(new Date(record.error.time), 'dd.MM.yyyy HH:mm')}</span>
                </Tooltip>
            )}
        </Box>
    );
};

const ClickableFunctionField = props => {
    const record = useRecordContext();

    const { onClick, ...rest } = props;
    const clickHandler = () => onClick(record);

    return <FunctionField onClick={clickHandler} sx={{ cursor: 'pointer' }} {...rest} />;
};

DeviceIssueErrorField.propTypes = {
    record: DeviceIssueModel,
};

ClickableFunctionField.propTypes = {
    onClick: PropTypes.func.isRequired,
};

ErrorPopper.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    record: DeviceIssueModel,
    onMouseEnter: PropTypes.func.isRequired,
    onMouseLeave: PropTypes.func.isRequired,
};
